import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Card, Row, Col, Badge, Spinner, Tab, Tabs, Table } from 'react-bootstrap';
import { FaCreditCard, FaMoneyBillWave, FaEye, FaHistory, FaFingerprint, FaShieldAlt, FaCheckCircle, FaUniversity, FaTimes, FaIdCard } from 'react-icons/fa';
import api from '../services/api';

const MicroATMModal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('cash_withdrawal');
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cardholderName: '',
    bankCode: ''
  });
  const [transactionDetails, setTransactionDetails] = useState({
    amount: '',
    pin: '',
    transactionType: 'CW',
    charges: 0
  });
  const [deviceStatus, setDeviceStatus] = useState('idle'); // idle, card_reading, pin_entry, processing
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [balanceEnquiry, setBalanceEnquiry] = useState(null);
  const [receiptData, setReceiptData] = useState(null);

  // Micro ATM supported banks
  const microATMBanks = [
    { code: 'SBI', name: 'State Bank of India', charges: { cw: 20, be: 8 }, dailyLimit: 10000 },
    { code: 'HDFC', name: 'HDFC Bank', charges: { cw: 18, be: 6 }, dailyLimit: 25000 },
    { code: 'ICICI', name: 'ICICI Bank', charges: { cw: 18, be: 6 }, dailyLimit: 25000 },
    { code: 'AXIS', name: 'Axis Bank', charges: { cw: 15, be: 5 }, dailyLimit: 20000 },
    { code: 'PNB', name: 'Punjab National Bank', charges: { cw: 20, be: 8 }, dailyLimit: 10000 },
    { code: 'BOB', name: 'Bank of Baroda', charges: { cw: 20, be: 8 }, dailyLimit: 10000 },
    { code: 'CANARA', name: 'Canara Bank', charges: { cw: 18, be: 6 }, dailyLimit: 15000 },
    { code: 'UNION', name: 'Union Bank of India', charges: { cw: 18, be: 6 }, dailyLimit: 15000 },
    { code: 'INDIAN', name: 'Indian Bank', charges: { cw: 15, be: 5 }, dailyLimit: 12000 },
    { code: 'IOB', name: 'Indian Overseas Bank', charges: { cw: 15, be: 5 }, dailyLimit: 12000 }
  ];

  const serviceTypes = [
    { key: 'cash_withdrawal', label: 'Cash Withdrawal', icon: <FaMoneyBillWave className="text-success" />, code: 'CW' },
    { key: 'balance_enquiry', label: 'Balance Enquiry', icon: <FaEye className="text-info" />, code: 'BE' },
    { key: 'pin_change', label: 'PIN Change', icon: <FaShieldAlt className="text-warning" />, code: 'PC' }
  ];

  const mockTransactionHistory = [
    {
      id: 'MATM001',
      type: 'Cash Withdrawal',
      cardNumber: '****5678',
      bank: 'HDFC Bank',
      amount: 3000,
      charges: 18,
      status: 'Success',
      date: '2025-01-15',
      time: '14:30:25'
    },
    {
      id: 'MATM002',
      type: 'Balance Enquiry',
      cardNumber: '****1234',
      bank: 'State Bank of India',
      amount: 0,
      charges: 8,
      status: 'Success',
      date: '2025-01-15',
      time: '10:15:42'
    },
    {
      id: 'MATM003',
      type: 'Cash Withdrawal',
      cardNumber: '****9876',
      bank: 'ICICI Bank',
      amount: 2500,
      charges: 18,
      status: 'Success',
      date: '2025-01-14',
      time: '16:45:12'
    }
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMessage({ type: '', text: '' });
    setDeviceStatus('idle');
    setBalanceEnquiry(null);
    setReceiptData(null);
    
    const serviceType = serviceTypes.find(s => s.key === tab);
    if (serviceType) {
      setTransactionDetails(prev => ({
        ...prev,
        transactionType: serviceType.code,
        amount: serviceType.code === 'CW' ? prev.amount : '0'
      }));
    }
    
    if (tab === 'history') {
      setTransactionHistory(mockTransactionHistory);
    }
  };

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.replace(/(.{4})/g, '$1 ').trim();
    return formatted.slice(0, 19);
  };

  const formatExpiryDate = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  const validateCard = () => {
    const cardNumber = cardDetails.cardNumber.replace(/\s/g, '');
    if (cardNumber.length < 16) {
      return 'Invalid card number';
    }
    
    if (!cardDetails.expiryDate || cardDetails.expiryDate.length !== 5) {
      return 'Invalid expiry date';
    }
    
    if (!cardDetails.cardholderName.trim()) {
      return 'Cardholder name is required';
    }
    
    return null;
  };

  const calculateCharges = (type, bankCode) => {
    const bank = microATMBanks.find(b => b.code === bankCode);
    if (!bank) return 0;
    
    switch (type) {
      case 'CW': return bank.charges.cw;
      case 'BE': return bank.charges.be;
      case 'PC': return 10; // Fixed PIN change charge
      default: return 0;
    }
  };

  const insertCard = async () => {
    const cardValidation = validateCard();
    if (cardValidation) {
      setMessage({ type: 'error', text: cardValidation });
      return;
    }

    setDeviceStatus('card_reading');
    setLoading(true);
    setMessage({ type: 'info', text: 'Reading card details...' });

    try {
      // Simulate card reading
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Auto-detect bank from card number
      const firstDigit = cardDetails.cardNumber.charAt(0);
      let detectedBank = '';
      
      if (firstDigit === '4' || firstDigit === '5') {
        detectedBank = 'HDFC'; // Simulation
      } else if (firstDigit === '6') {
        detectedBank = 'SBI';
      } else {
        detectedBank = 'ICICI';
      }
      
      setCardDetails(prev => ({...prev, bankCode: detectedBank}));
      
      const charges = calculateCharges(transactionDetails.transactionType, detectedBank);
      setTransactionDetails(prev => ({...prev, charges}));
      
      setDeviceStatus('pin_entry');
      setMessage({ type: 'success', text: 'Card read successfully. Please enter PIN.' });
    } catch (error) {
      setDeviceStatus('idle');
      setMessage({ type: 'error', text: 'Card reading failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const processTransaction = async () => {
    if (!transactionDetails.pin || transactionDetails.pin.length !== 4) {
      setMessage({ type: 'error', text: 'Please enter a valid 4-digit PIN' });
      return;
    }

    if (transactionDetails.transactionType === 'CW') {
      const amount = parseFloat(transactionDetails.amount);
      const bank = microATMBanks.find(b => b.code === cardDetails.bankCode);
      
      if (!amount || amount < 100 || amount > 10000) {
        setMessage({ type: 'error', text: 'Withdrawal amount must be between ₹100 and ₹10,000' });
        return;
      }
      
      if (bank && amount > bank.dailyLimit) {
        setMessage({ type: 'error', text: `Daily limit for ${bank.name} is ₹${bank.dailyLimit.toLocaleString()}` });
        return;
      }
    }

    setDeviceStatus('processing');
    setLoading(true);
    setMessage({ type: 'info', text: 'Processing transaction...' });

    try {
      const bank = microATMBanks.find(b => b.code === cardDetails.bankCode);
      const amount = parseFloat(transactionDetails.amount || 0);
      const charges = transactionDetails.charges;
      
      // Generate idempotency key
      const idempotencyKey = `MATM_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Call real PaySprint API
      const response = await api.post('/micro-atm/process', {
        mobile: cardDetails.cardNumber.replace(/\s/g, '').slice(-10) || '9999906998', // Use last 10 digits or default
        amount: amount,
        transaction_type: transactionDetails.transactionType, // CW or BE
        idempotency_key: idempotencyKey,
        latitude: '28.7041', // Default location
        longitude: '77.1025'
      });

      if (response.data.success) {
        const transactionData = {
          transactionId: response.data.transaction_id || response.data.provider_txn_id || 'MATM' + Date.now(),
          type: serviceTypes.find(s => s.code === transactionDetails.transactionType)?.label,
          cardNumber: cardDetails.cardNumber,
          bank: bank?.name || 'Unknown Bank',
          amount: amount,
          charges: charges,
          status: 'Success',
          timestamp: response.data.timestamp || new Date().toLocaleString(),
          newBalance: response.data.new_balance,
          formattedBalance: response.data.formatted_balance
        };

        // Handle different transaction types
        if (transactionDetails.transactionType === 'BE') {
          // For balance enquiry, extract balance from PaySprint response
          const balance = response.data.paysprint_response?.balance || response.data.paysprint_response?.available_balance || null;
          setBalanceEnquiry({
            cardNumber: '****' + cardDetails.cardNumber.slice(-4),
            availableBalance: balance || 0,
            bank: bank?.name || 'Unknown Bank',
            timestamp: transactionData.timestamp
          });
        } else if (transactionDetails.transactionType === 'PC') {
          setMessage({ 
            type: 'success', 
            text: 'PIN changed successfully! Transaction ID: ' + transactionData.transactionId 
          });
        }

        // Generate receipt for cash withdrawal
        if (transactionDetails.transactionType === 'CW') {
          setReceiptData({
            transactionId: transactionData.transactionId,
            cardNumber: '****' + cardDetails.cardNumber.slice(-4),
            cardholderName: cardDetails.cardholderName,
            bank: bank?.name || 'Unknown Bank',
            amount: amount,
            charges: charges,
            total: amount + charges,
            timestamp: transactionData.timestamp,
            location: 'Micro ATM - Agent Location'
          });
        }

        setMessage({ 
          type: 'success', 
          text: `Micro ATM ${transactionData.type} successful! Transaction ID: ${transactionData.transactionId}` 
        });

        if (onSuccess) {
          onSuccess(transactionData);
        }

        setDeviceStatus('completed');

        // Auto-close for PIN change
        if (transactionDetails.transactionType === 'PC') {
          setTimeout(() => {
            onHide();
          }, 3000);
        }
      } else {
        throw new Error(response.data.message || 'Transaction failed');
      }

    } catch (error) {
      setDeviceStatus('idle');
      const errorMessage = error.response?.data?.message || error.message || 'Transaction failed. Please try again.';
      setMessage({ type: 'error', text: errorMessage });
      console.error('Micro ATM transaction error:', error);
    } finally {
      setLoading(false);
    }
  };

  const ejectCard = () => {
    setDeviceStatus('idle');
    setCardDetails({ cardNumber: '', expiryDate: '', cardholderName: '', bankCode: '' });
    setTransactionDetails(prev => ({ ...prev, pin: '', charges: 0 }));
    setMessage({ type: 'info', text: 'Card ejected. Please collect your card.' });
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Success': 'success',
      'Failed': 'danger',
      'Pending': 'warning'
    };
    return <Badge bg={variants[status]}>{status}</Badge>;
  };

  const getDeviceStatusIcon = () => {
    switch (deviceStatus) {
      case 'card_reading':
        return <Spinner animation="border" variant="primary" />;
      case 'pin_entry':
        return <FaIdCard className="text-success" size={40} />;
      case 'processing':
        return <Spinner animation="border" variant="warning" />;
      case 'completed':
        return <FaCheckCircle className="text-success" size={40} />;
      default:
        return <FaCreditCard className="text-secondary" size={40} />;
    }
  };

  const handleReset = () => {
    setCardDetails({ cardNumber: '', expiryDate: '', cardholderName: '', bankCode: '' });
    setTransactionDetails({ amount: '', pin: '', transactionType: 'CW', charges: 0 });
    setDeviceStatus('idle');
    setBalanceEnquiry(null);
    setReceiptData(null);
    setMessage({ type: '', text: '' });
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="bg-info text-white">
        <Modal.Title>
          <FaCreditCard className="me-2" />
          Micro ATM Services
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Micro ATM Service Tabs */}
        <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-4">
          {serviceTypes.map((service) => (
            <Tab 
              key={service.key}
              eventKey={service.key} 
              title={
                <span>
                  {service.icon}
                  <span className="ms-2">{service.label}</span>
                </span>
              }
            >
              <div className="p-2">
                <h6 className="text-info mb-3">{service.label}</h6>
              </div>
            </Tab>
          ))}
          <Tab eventKey="history" title={
            <span>
              <FaHistory className="me-2" />
              Transaction History
            </span>
          }>
            <div className="p-2">
              <h6 className="text-info mb-3">Micro ATM Transaction History</h6>
            </div>
          </Tab>
        </Tabs>

        {/* Transaction History */}
        {activeTab === 'history' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6>Recent Micro ATM Transactions</h6>
              <Badge bg="info">Total: ₹5,500</Badge>
            </div>
            
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Type</th>
                  <th>Card Number</th>
                  <th>Bank</th>
                  <th>Amount</th>
                  <th>Charges</th>
                  <th>Status</th>
                  <th>Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {transactionHistory.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{transaction.id}</td>
                    <td>{transaction.type}</td>
                    <td>{transaction.cardNumber}</td>
                    <td>{transaction.bank}</td>
                    <td>₹{transaction.amount.toLocaleString()}</td>
                    <td>₹{transaction.charges}</td>
                    <td>{getStatusBadge(transaction.status)}</td>
                    <td>
                      <small>{transaction.date}<br/>{transaction.time}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Micro ATM Transaction Interface */}
        {activeTab !== 'history' && (
          <Row>
            {/* Card Details Input */}
            <Col md={6}>
              <Card className="mb-4">
                <Card.Header>
                  <FaCreditCard className="me-2" />
                  Card Details
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Card Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 16-digit card number"
                      value={cardDetails.cardNumber}
                      onChange={(e) => setCardDetails({
                        ...cardDetails, 
                        cardNumber: formatCardNumber(e.target.value)
                      })}
                      maxLength={19}
                      disabled={deviceStatus !== 'idle'}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Expiry Date *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="MM/YY"
                          value={cardDetails.expiryDate}
                          onChange={(e) => setCardDetails({
                            ...cardDetails, 
                            expiryDate: formatExpiryDate(e.target.value)
                          })}
                          maxLength={5}
                          disabled={deviceStatus !== 'idle'}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Cardholder Name *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Name on card"
                          value={cardDetails.cardholderName}
                          onChange={(e) => setCardDetails({
                            ...cardDetails, 
                            cardholderName: e.target.value.toUpperCase()
                          })}
                          disabled={deviceStatus !== 'idle'}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {activeTab === 'cash_withdrawal' && (
                    <Form.Group className="mb-3">
                      <Form.Label>Withdrawal Amount (₹) *</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter amount (₹100 - ₹10,000)"
                        value={transactionDetails.amount}
                        onChange={(e) => setTransactionDetails({
                          ...transactionDetails, 
                          amount: e.target.value
                        })}
                        min="100"
                        max="10000"
                        disabled={deviceStatus === 'idle'}
                      />
                    </Form.Group>
                  )}

                  {deviceStatus === 'pin_entry' && (
                    <Form.Group className="mb-3">
                      <Form.Label>Enter PIN *</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="4-digit PIN"
                        value={transactionDetails.pin}
                        onChange={(e) => setTransactionDetails({
                          ...transactionDetails, 
                          pin: e.target.value.replace(/\D/g, '').slice(0, 4)
                        })}
                        maxLength={4}
                      />
                    </Form.Group>
                  )}

                  {cardDetails.bankCode && transactionDetails.charges > 0 && (
                    <Alert variant="info">
                      <strong>Bank:</strong> {microATMBanks.find(b => b.code === cardDetails.bankCode)?.name}<br/>
                      <strong>Service Charges:</strong> ₹{transactionDetails.charges}
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Device Interface */}
            <Col md={6}>
              <Card className="mb-4">
                <Card.Header>
                  <FaUniversity className="me-2" />
                  Micro ATM Device
                </Card.Header>
                <Card.Body className="text-center">
                  <div className="mb-4">
                    {getDeviceStatusIcon()}
                  </div>
                  
                  <div className="mb-3">
                    <Badge bg={
                      deviceStatus === 'completed' ? 'success' :
                      deviceStatus === 'processing' ? 'warning' :
                      deviceStatus === 'pin_entry' ? 'info' :
                      deviceStatus === 'card_reading' ? 'primary' : 'secondary'
                    }>
                      {deviceStatus === 'completed' ? 'Transaction Complete' :
                       deviceStatus === 'processing' ? 'Processing' :
                       deviceStatus === 'pin_entry' ? 'Enter PIN' :
                       deviceStatus === 'card_reading' ? 'Reading Card' : 'Insert Card'}
                    </Badge>
                  </div>

                  {deviceStatus === 'idle' && (
                    <Button 
                      variant="info" 
                      onClick={insertCard}
                      disabled={loading}
                      className="mb-3"
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Reading...
                        </>
                      ) : (
                        <>
                          <FaCreditCard className="me-2" />
                          Insert Card
                        </>
                      )}
                    </Button>
                  )}

                  {deviceStatus === 'pin_entry' && (
                    <div>
                      <Button 
                        variant="success" 
                        onClick={processTransaction}
                        disabled={loading || !transactionDetails.pin}
                        className="mb-2 me-2"
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <FaCheckCircle className="me-2" />
                            Confirm
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        onClick={ejectCard}
                        className="mb-2"
                      >
                        <FaTimes className="me-2" />
                        Cancel
                      </Button>
                    </div>
                  )}

                  {deviceStatus === 'completed' && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={ejectCard}
                      className="mb-3"
                    >
                      <FaCreditCard className="me-2" />
                      Eject Card
                    </Button>
                  )}

                  <div className="mt-3">
                    <small className="text-muted">
                      Secure EMV chip card processing
                    </small>
                  </div>
                </Card.Body>
              </Card>

              {/* Transaction Results */}
              {balanceEnquiry && (
                <Card className="mb-4">
                  <Card.Header className="bg-info text-white">
                    <FaEye className="me-2" />
                    Balance Enquiry Result
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-2"><strong>Card:</strong> {balanceEnquiry.cardNumber}</div>
                    <div className="mb-2"><strong>Bank:</strong> {balanceEnquiry.bank}</div>
                    <div className="mb-2"><strong>Available Balance:</strong> ₹{balanceEnquiry.availableBalance.toLocaleString()}</div>
                    <div className="mb-2"><strong>Enquiry Time:</strong> {balanceEnquiry.timestamp}</div>
                  </Card.Body>
                </Card>
              )}

              {receiptData && (
                <Card>
                  <Card.Header className="bg-success text-white">
                    <FaMoneyBillWave className="me-2" />
                    Transaction Receipt
                  </Card.Header>
                  <Card.Body>
                    <div className="text-center mb-3">
                      <h6>MICRO ATM RECEIPT</h6>
                      <small>{receiptData.location}</small>
                    </div>
                    <hr/>
                    <div className="mb-1"><strong>Transaction ID:</strong> {receiptData.transactionId}</div>
                    <div className="mb-1"><strong>Card:</strong> {receiptData.cardNumber}</div>
                    <div className="mb-1"><strong>Cardholder:</strong> {receiptData.cardholderName}</div>
                    <div className="mb-1"><strong>Bank:</strong> {receiptData.bank}</div>
                    <div className="mb-1"><strong>Amount:</strong> ₹{receiptData.amount.toLocaleString()}</div>
                    <div className="mb-1"><strong>Charges:</strong> ₹{receiptData.charges}</div>
                    <hr/>
                    <div className="mb-1"><strong>Total:</strong> ₹{receiptData.total.toLocaleString()}</div>
                    <div className="mb-1"><strong>Date & Time:</strong> {receiptData.timestamp}</div>
                    <div className="text-center mt-3">
                      <small className="text-muted">Thank you for using Micro ATM</small>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        )}

        {/* Message */}
        {message.text && (
          <Alert variant={
            message.type === 'error' ? 'danger' : 
            message.type === 'info' ? 'info' : 'success'
          } className="mt-3">
            {message.text}
          </Alert>
        )}

        {/* Security Notice */}
        <Alert variant="info" className="mb-0">
          <FaShieldAlt className="me-2" />
          <strong>Secure Micro ATM:</strong> All card transactions are processed through secure EMV chip technology with end-to-end encryption.
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {activeTab !== 'history' && deviceStatus === 'idle' && (
              <Button variant="outline-secondary" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide}>
              Close
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default MicroATMModal;
